import React, { useState, useCallback, useEffect } from 'react';
import Header from '../components/Header';
import JiraDataList from '../components/JiraDataList';
import { useApiRequest } from '../actions/apiRequest';
import type { Change } from '../types';

interface Filter {
  key: string;
  label: string;
}

const filters: Filter[] = [
  { key: 'all', label: 'Alle' },
  { key: 'open', label: 'Stembaar' },
  { key: 'closed', label: 'Niet stembaar' },
];

const JiraData: React.FC = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [data, setData] = useState<Change[]>([]);
  const [loading, setLoading] = useState(true);
  const apiRequest = useApiRequest();

  const fetchData = useCallback(
    async (filter: string): Promise<void> => {
      setLoading(true);
      try {
        let path = '/changes';
        switch (filter) {
          case 'open':
            path = '/changes?filter=votable';
            break;
          case 'closed':
            path = '/changes?filter=unvotable';
            break;
          case 'all':
          default:
            path = '/changes';
            break;
        }
        const result = await apiRequest<Change[]>({ path });
        setData(result || []);
      } catch (error) {
        console.error('Error fetching data:', error);
        setData([]);
      } finally {
        setLoading(false);
      }
    },
    [apiRequest]
  );

  useEffect(() => {
    fetchData(activeFilter);
  }, [activeFilter, fetchData]);

  const handleFilterChange = (filter: string): void => {
    setActiveFilter(filter);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="p-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-4xl font-bold text-primary-600 mb-6">Connect+ tickets</h1>
          <div className="mb-8">
            <div className="flex flex-col sm:inline-flex sm:flex-row gap-2 sm:gap-0 rounded-lg bg-neutral-100 p-1">
              {filters.map((filter) => (
                <button
                  key={filter.key}
                  onClick={() => {
                    if (loading) return;
                    handleFilterChange(filter.key);
                  }}
                  className={`w-full text-center sm:w-auto px-6 py-2 rounded-md text-sm font-medium transition-all ${
                    activeFilter === filter.key
                      ? 'bg-white text-primary-600 shadow-sm'
                      : 'text-neutral-600 hover:text-neutral-900'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>
          {loading ? (
            <div className="text-center py-12">
              <i className="fa-light fa-spinner-third fa-spin text-4xl text-primary-600 mb-3" />
              <p className="text-neutral-400 text-lg">Laden...</p>
            </div>
          ) : (
            <JiraDataList
              data={data}
              showVoteInfo={activeFilter === 'open' || activeFilter === 'all'}
              activeFilter={activeFilter}
              fetchData={fetchData}
            />
          )}
        </div>
      </main>
    </div>
  );
};

export default JiraData;
